import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";

import { newValidate, newGetInitialValues, useAmsSelector } from "@awe-web/shared/lib";
import { Translation as T } from "@awe-web/shared/lib";
import { provisioningTypes } from "@awe-web/shared/lib";
import { useInitialize } from "../../../util/react.js";
import { B2C_LTR_SelectExtras } from "../../../routing/routes.js";
import { Row, Col } from "../../../components/grid.js";
import { ScreenSection, ScreenLoading } from "../../../components/screen.js";
import { TypeArea } from "../../../components/layout.js";
import { Button, RequiredNotice } from "../../../components/form.js";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import { getProvisioningTypeB2c } from "../../../rent_contracts/store.js";
import { initialize, submit } from "./actions.js";
import SelectProvisioningForm from "./select_provisioning_form";

import "./select_provisioning.scss";

const BACK = "ALG0000030";
const NOTICE = "FSD0000209";
const PRICES_NOTICE = "ALG0000244";

function InternalSelectProvisioning({ onSubmit }) {
  const provisioningType = useSelector((state) => getProvisioningTypeB2c(state));

  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("deliveryAddress12");
    const inialValuesFactory = formInitializer(state);
    const result = inialValuesFactory({
      "DeliveryContactPerson:name": "",
      "DeliveryContactPerson:surname": "",
      "DeliveryAddress:street": "",
      "DeliveryAddress:building": "",
      "DeliveryAddress:zipCode": "",
      "DeliveryAddress:city": "",
      "DeliveryAddress:countryCode": "",
      "DeliveryContactPerson:phone": ""
    });

    return { ...result, provisioningType };
  });

  const validateDelivery = useAmsSelector((state) => newValidate("deliveryAddress12")(state));

  const validateForm = useCallback((values) => {
    if (values.provisioningType === provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS) {
      return validateDelivery(values);
    } else {
      return {};
    }
  }, [validateDelivery]);

  return (
    <ScreenSection>
      <TypeArea>
        <Formik initialValues={initialValues} validate={validateForm} onSubmit={onSubmit}>
          {(formikProps) => (
            <SelectProvisioningForm {...formikProps} />
          )}
        </Formik>

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <RequiredNotice className="b2c-ltr-select-provisioning__required-notice">
              <T code={NOTICE} />
            </RequiredNotice>
            <div><T code={PRICES_NOTICE} /></div>
          </Col>
        </Row>
      </TypeArea>
    </ScreenSection>
  );
}

export default function B2C_LTR_SelectProvisioning() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (formValues) => {
    const nextPath = await dispatch(submit(formValues));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2c-ltr-select-provisioning">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2C" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2c-ltr-select-provisioning__selected-rent-object"
            selectedBasicProperties="B2C"
            action={(
              <Button secondary back to={B2C_LTR_SelectExtras.path()}>
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <InternalSelectProvisioning onSubmit={onSubmit} />
    </div>
  );
}
